// ===========================================================================
// ProCare.SG — image uploads
// ---------------------------------------------------------------------------
// Every image is validated and re-encoded in the browser before it reaches
// Cloud Storage: oversized camera photos become right-sized WebP, which keeps
// the public site fast and storage costs low. Storage rules re-check type and
// size server-side, so a tampered client cannot bypass any of this.
// ===========================================================================
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "@/lib/firebase";

export const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // matches storage.rules
export const MAX_ENQUIRY_BYTES = 5 * 1024 * 1024;

/** Human-readable guard used by every upload entry point. */
export function validateImage(file, maxBytes = MAX_UPLOAD_BYTES) {
    if (!file) return "Please choose a file.";
    if (!ACCEPTED_TYPES.includes(file.type)) {
        return "Please upload a JPG, PNG, WebP, GIF or AVIF image.";
    }
    if (file.size > maxBytes) {
        return `Image must be under ${Math.round(maxBytes / (1024 * 1024))}MB.`;
    }
    return null;
}

function safeName(name) {
    return String(name || "image")
        .toLowerCase()
        .replace(/\.[^.]+$/, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60) || "image";
}

/**
 * Downscale and re-encode to WebP. Animated GIFs are passed through untouched
 * (a canvas would flatten them to a single frame).
 */
async function optimise(file, maxWidth = 1600, quality = 0.82) {
    if (file.type === "image/gif" || typeof createImageBitmap !== "function") return file;

    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, maxWidth / bitmap.width);
        const width = Math.round(bitmap.width * scale);
        const height = Math.round(bitmap.height * scale);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        context.imageSmoothingQuality = "high";
        context.drawImage(bitmap, 0, 0, width, height);
        bitmap.close?.();

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", quality));
        if (!blob || blob.size >= file.size) return file; // never make it worse
        return new File([blob], `${safeName(file.name)}.webp`, { type: "image/webp" });
    } catch {
        return file; // any failure: upload the original and let the rules decide
    }
}

/**
 * Upload an image and return its public URL plus the storage path (kept so the
 * file can be removed later when the owning document is edited or deleted).
 *
 * @param {File} file
 * @param {{folder?: string, maxWidth?: number, onProgress?: (percent: number) => void}} options
 * @returns {Promise<{url: string, path: string, name: string, size: number}>}
 */
export async function uploadImage(file, { folder = "general", maxWidth = 1600, onProgress } = {}) {
    const error = validateImage(file);
    if (error) throw new Error(error);

    const optimised = await optimise(file, maxWidth);
    const extension = optimised.type === "image/webp" ? "webp" : (optimised.name.split(".").pop() || "jpg");
    const path = `media/${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName(file.name)}.${extension}`;

    const task = uploadBytesResumable(ref(storage, path), optimised, {
        contentType: optimised.type,
        cacheControl: "public, max-age=31536000, immutable",
    });

    await new Promise((resolve, reject) => {
        task.on(
            "state_changed",
            (snapshot) => {
                if (onProgress && snapshot.totalBytes) {
                    onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
                }
            },
            reject,
            resolve,
        );
    });

    return {
        url: await getDownloadURL(task.snapshot.ref),
        path,
        name: file.name,
        size: optimised.size,
    };
}

/**
 * Photo attached to a public contact-form submission.
 *
 * Returns the storage *path*, not a download URL. Enquiry photos are private:
 * storage rules let the public add one but not read it back, so asking for a
 * download URL here would be denied. The path travels with the enquiry and the
 * dashboard — which does have read access — resolves it when an administrator
 * opens the enquiry.
 */
export async function uploadEnquiryPhoto(file, onProgress) {
    const error = validateImage(file, MAX_ENQUIRY_BYTES);
    if (error) throw new Error(error);

    const optimised = await optimise(file, 1400, 0.78);
    const stamp = new Date();
    const bucketFolder = `${stamp.getFullYear()}-${String(stamp.getMonth() + 1).padStart(2, "0")}`;
    const extension = optimised.type === "image/webp" ? "webp" : (optimised.name.split(".").pop() || "jpg");
    const path = `enquiry-uploads/${bucketFolder}/${Date.now()}-${Math.random().toString(36).slice(2, 10)}.${extension}`;

    const task = uploadBytesResumable(ref(storage, path), optimised, { contentType: optimised.type });

    await new Promise((resolve, reject) => {
        task.on(
            "state_changed",
            (snapshot) => {
                if (onProgress && snapshot.totalBytes) {
                    onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
                }
            },
            reject,
            resolve,
        );
    });

    return { path };
}

/** Resolve a private storage path to a URL. Administrators only, by rules. */
export async function resolveStorageUrl(path) {
    if (!path) return "";
    try {
        return await getDownloadURL(ref(storage, path));
    } catch (error) {
        console.warn("[storage] could not resolve", path, error?.code);
        return "";
    }
}

/**
 * Best-effort cleanup — a missing object is not an error worth surfacing.
 * Restricted to the two areas this app owns so a stray value can never point
 * the delete at something else in the bucket.
 */
export async function deleteImage(path) {
    if (!path || !(path.startsWith("media/") || path.startsWith("enquiry-uploads/"))) return;
    try {
        await deleteObject(ref(storage, path));
    } catch (error) {
        if (error?.code !== "storage/object-not-found") {
            console.warn("[storage] could not delete", path, error?.code);
        }
    }
}
